/**
 * MODAL: Détail des appels d'un utilisateur
 * Affiche la liste des appels effectués par un utilisateur sur une période donnée
 */

import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@/components/ui/modal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EnterpriseTable } from '@/components/tables/EnterpriseTable';
import {
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneOff,
  Voicemail,
  Clock,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import type { CallDetail } from '@/hooks/useCallStats';
import { CALL_DIRECTION_LABELS, CALL_OUTCOME_LABELS } from '@/types/timeline';
import { PERIOD_OPTIONS, type PeriodValue } from '@/components/ui';

// Item #R5 Phase B : pagination popin appels — défaut 10 par page
const PAGE_SIZE = 10;

interface UserCallsDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  userName: string;
  userId: string;
  calls: CallDetail[];
  period: PeriodValue;
  /**
   * Item #17 : pré-filtre la liste sur un type d'issue d'appel à l'ouverture.
   * undefined => tous les appels
   */
  initialOutcomeFilter?: 'answered' | 'no_answer' | 'voicemail';
}

const OUTCOME_TITLE_LABELS: Record<'answered' | 'no_answer' | 'voicemail', string> = {
  answered: 'répondus',
  no_answer: 'sans réponse',
  voicemail: 'avec messagerie',
};

/**
 * Formate une date en format lisible
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/**
 * Retourne l'icône selon la direction de l'appel
 */
function DirectionIcon({ direction }: { direction: string }) {
  const iconClass = 'h-4 w-4';
  if (direction === 'incoming') {
    return <PhoneIncoming className={iconClass} />;
  }
  return <PhoneOutgoing className={iconClass} />;
}

/**
 * Retourne l'icône et le style selon le résultat de l'appel
 */
function OutcomeBadge({ outcome }: { outcome: string }) {
  const iconClass = 'h-3 w-3';

  switch (outcome) {
    case 'answered':
      return (
        <Badge variant="outline" className="gap-1 border-green-200 bg-green-50 text-green-700">
          <Phone className={iconClass} />
          {CALL_OUTCOME_LABELS.answered}
        </Badge>
      );
    case 'no_answer':
      return (
        <Badge variant="outline" className="gap-1 border-red-200 bg-red-50 text-red-700">
          <PhoneOff className={iconClass} />
          {CALL_OUTCOME_LABELS.no_answer}
        </Badge>
      );
    case 'voicemail':
      return (
        <Badge variant="outline" className="gap-1 border-amber-200 bg-amber-50 text-amber-700">
          <Voicemail className={iconClass} />
          {CALL_OUTCOME_LABELS.voicemail}
        </Badge>
      );
    default:
      return <Badge variant="outline">{outcome}</Badge>;
  }
}

/**
 * Modal affichant le détail des appels d'un utilisateur
 */
function UserCallsDetailModal({
  isOpen,
  onClose,
  userName,
  calls,
  period,
  initialOutcomeFilter,
}: UserCallsDetailModalProps) {
  // Item #R5 Phase B : pagination locale — slice après filtre
  const [currentPage, setCurrentPage] = useState(1);

  // Obtenir le label de la période
  const periodLabel = useMemo(() => {
    const option = PERIOD_OPTIONS.find((opt) => opt.value === period);
    return option?.label || period;
  }, [period]);

  // Item #17 : appels effectivement listés (filtrés si initialOutcomeFilter défini)
  const filteredCalls = useMemo(() => {
    if (!initialOutcomeFilter) return calls;
    return calls.filter((c) => c.metadata?.outcome === initialOutcomeFilter);
  }, [calls, initialOutcomeFilter]);

  // Pagination : total + slice + reset page quand le filtre/ouverture change
  const totalPages = Math.max(1, Math.ceil(filteredCalls.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedCalls = useMemo(
    () => filteredCalls.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filteredCalls, safePage]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [initialOutcomeFilter, isOpen]);

  // Calculer les statistiques (toujours sur l'ensemble pour montrer le contexte global)
  const stats = useMemo(() => {
    const answered = calls.filter((c) => c.metadata?.outcome === 'answered').length;
    const noAnswer = calls.filter((c) => c.metadata?.outcome === 'no_answer').length;
    const voicemail = calls.filter((c) => c.metadata?.outcome === 'voicemail').length;
    const incoming = calls.filter((c) => c.metadata?.direction === 'incoming').length;
    const outgoing = calls.filter((c) => c.metadata?.direction === 'outgoing').length;

    return { answered, noAnswer, voicemail, incoming, outgoing, total: calls.length };
  }, [calls]);

  // Titre de la modal — précise le filtre actif si applicable
  const modalTitle = initialOutcomeFilter
    ? `Appels ${OUTCOME_TITLE_LABELS[initialOutcomeFilter]} de ${userName}`
    : `Appels de ${userName}`;

  // Colonnes du tableau
  const columns = useMemo<ColumnDef<CallDetail>[]>(
    () => [
      {
        id: 'created_at',
        header: 'Date',
        accessorKey: 'created_at',
        cell: ({ row }) => (
          <span className="text-sm text-zinc-600">{formatDate(row.original.created_at)}</span>
        ),
      },
      {
        id: 'direction',
        header: 'Direction',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <DirectionIcon direction={row.original.metadata?.direction} />
            <span className="text-sm">
              {CALL_DIRECTION_LABELS[row.original.metadata?.direction] || '-'}
            </span>
          </div>
        ),
      },
      {
        id: 'outcome',
        header: 'Résultat',
        cell: ({ row }) => <OutcomeBadge outcome={row.original.metadata?.outcome} />,
      },
      {
        id: 'entity',
        header: 'Entité',
        cell: ({ row }) => {
          // Utiliser le nom résolu (enrichi par le hook) ou fallback
          const entityName =
            row.original.resolved_entity_name || row.original.metadata?.entity_name;

          if (!entityName) {
            return <span className="text-sm text-zinc-400">-</span>;
          }

          // Badge avec le type d'entité
          const typeLabels: Record<string, string> = {
            crew: 'Équipage',
            contact: 'Contact',
            prospect: 'Prospect',
          };
          const typeLabel = typeLabels[row.original.entity_type] || row.original.entity_type;

          return (
            <div className="flex flex-col">
              <span className="text-sm font-medium">{entityName}</span>
              <span className="text-xs text-zinc-400">{typeLabel}</span>
            </div>
          );
        },
      },
      {
        id: 'duration',
        header: 'Durée',
        cell: ({ row }) => {
          const duration = row.original.metadata?.duration;
          if (!duration || duration === 0) return <span className="text-zinc-400">-</span>;
          return (
            <div className="flex items-center gap-1 text-sm text-zinc-600">
              <Clock className="h-3 w-3" />
              {duration} min
            </div>
          );
        },
      },
      {
        id: 'notes',
        header: 'Notes',
        cell: ({ row }) => {
          const notes = row.original.metadata?.notes;
          if (!notes) return <span className="text-zinc-400">-</span>;
          return (
            <span className="text-sm text-zinc-600 line-clamp-2" title={notes}>
              {notes}
            </span>
          );
        },
      },
    ],
    []
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={modalTitle} size="6xl">
      <div className="space-y-6">
        {/* En-tête avec période et stats */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-zinc-500">Période : {periodLabel}</p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="gap-1">
                <Phone className="h-3 w-3" />
                {initialOutcomeFilter
                  ? `${filteredCalls.length} / ${stats.total}`
                  : `${stats.total}`}{' '}
                appels
              </Badge>
            </div>
          </div>
        </div>

        {/* Statistiques détaillées */}
        <div className="grid grid-cols-5 gap-4">
          <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 text-center">
            <div className="text-2xl font-bold text-zinc-900">{stats.total}</div>
            <div className="text-xs text-zinc-500">Total</div>
          </div>
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-center">
            <div className="text-2xl font-bold text-green-700">{stats.answered}</div>
            <div className="text-xs text-green-600">Répondus</div>
          </div>
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
            <div className="text-2xl font-bold text-red-700">{stats.noAnswer}</div>
            <div className="text-xs text-red-600">Non aboutis</div>
          </div>
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-center">
            <div className="text-2xl font-bold text-blue-700">{stats.incoming}</div>
            <div className="text-xs text-blue-600">Entrants</div>
          </div>
          <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 text-center">
            <div className="text-2xl font-bold text-purple-700">{stats.outgoing}</div>
            <div className="text-xs text-purple-600">Sortants</div>
          </div>
        </div>

        {/* Tableau des appels */}
        {filteredCalls.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-zinc-500">
            <Phone className="mb-4 h-12 w-12 text-zinc-300" />
            <p className="text-sm">
              {initialOutcomeFilter
                ? `Aucun appel ${OUTCOME_TITLE_LABELS[initialOutcomeFilter]} sur cette période`
                : 'Aucun appel sur cette période'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="max-h-96 overflow-auto" data-testid="user-calls-table">
              <EnterpriseTable
                data={paginatedCalls}
                columns={columns}
                pageSize={PAGE_SIZE}
                currentPage={safePage}
                manualPagination
              />
            </div>

            {/* Footer pagination (Item #R5 Phase B) */}
            {totalPages > 1 && (
              <div
                className="flex items-center justify-between border-t border-zinc-200 pt-3 text-sm"
                data-testid="user-calls-pagination"
              >
                <span className="text-zinc-500" data-testid="user-calls-pagination-total">
                  {filteredCalls.length} appel
                  {filteredCalls.length > 1 ? 's' : ''} au total
                </span>
                <div className="flex items-center gap-3">
                  <span className="text-zinc-600" data-testid="user-calls-pagination-indicator">
                    Page {safePage} / {totalPages}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safePage <= 1}
                    aria-label="Page précédente"
                    data-testid="user-calls-pagination-prev"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Précédent
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage >= totalPages}
                    aria-label="Page suivante"
                    data-testid="user-calls-pagination-next"
                  >
                    Suivant
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

export { UserCallsDetailModal };
